import type { DeliveryCity } from '@c214/shared';
import { DELIVERY_OPTIONS, type DeliveryKey } from '@c214/shared';
import { useState } from 'react';
import { Button } from '../ui/button';
import { Chip } from '../ui/chip';
import { Input } from '../ui/input';
import { PhoneInput } from '../ui/phone-input';
import { CityAutocomplete } from './CityAutocomplete';
import { WarehousePicker } from './WarehousePicker';

interface Props {
  isSelf: boolean;
  onIsSelfChange: (isSelf: boolean) => void;
  lastName: string;
  onLastNameChange: (value: string) => void;
  firstName: string;
  onFirstNameChange: (value: string) => void;
  patronymic: string;
  onPatronymicChange: (value: string) => void;
  phone: string;
  onPhoneChange: (value: string) => void;
  deliveryKey: DeliveryKey;
  onDeliveryKeyChange: (key: DeliveryKey) => void;
  city: DeliveryCity | null;
  onCityChange: (city: DeliveryCity | null) => void;
  warehouseRef: string | null;
  onWarehouseChange: (ref: string, address: string) => void;
  street: string;
  onStreetChange: (value: string) => void;
  building: string;
  onBuildingChange: (value: string) => void;
  apartment: string;
  onApartmentChange: (value: string) => void;
  floor: string;
  onFloorChange: (value: string) => void;
  intercom: string;
  onIntercomChange: (value: string) => void;
  onNext: () => void;
}

export function DeliveryStep(props: Props) {
  const [attempted, setAttempted] = useState(false);

  const needsWarehouse = props.deliveryKey === 'np_branch';
  const needsCourierAddress = props.deliveryKey === 'np_courier';

  const lastNameMissing = !props.lastName.trim();
  const firstNameMissing = !props.firstName.trim();
  const phoneMissing = !props.phone.trim();
  const cityMissing = !props.city;
  const warehouseMissing = needsWarehouse && !props.warehouseRef;
  const streetMissing = needsCourierAddress && !props.street.trim();
  const buildingMissing = needsCourierAddress && !props.building.trim();

  const canProceed =
    !lastNameMissing &&
    !firstNameMissing &&
    !phoneMissing &&
    !cityMissing &&
    !warehouseMissing &&
    !streetMissing &&
    !buildingMissing;

  const handleNext = () => {
    if (!canProceed) {
      setAttempted(true);
      return;
    }
    props.onNext();
  };

  return (
    <div>
      <p className="mono-label mb-6 text-accent">01 ДОСТАВКА</p>

      <div className="mb-3 flex gap-2">
        <Chip active={props.isSelf} onClick={() => props.onIsSelfChange(true)}>
          Я
        </Chip>
        <Chip active={!props.isSelf} onClick={() => props.onIsSelfChange(false)}>
          Інша людина
        </Chip>
      </div>

      <div className="space-y-3">
        <div>
          <Input
            placeholder="Прізвище"
            value={props.lastName}
            onChange={(e) => props.onLastNameChange(e.target.value)}
            className={attempted && lastNameMissing ? 'border-warn' : ''}
          />
          {attempted && lastNameMissing && (
            <p className="mt-1 text-[12px] text-warn">Вкажіть прізвище</p>
          )}
        </div>

        <div>
          <Input
            placeholder="Імʼя"
            value={props.firstName}
            onChange={(e) => props.onFirstNameChange(e.target.value)}
            className={attempted && firstNameMissing ? 'border-warn' : ''}
          />
          {attempted && firstNameMissing && (
            <p className="mt-1 text-[12px] text-warn">Вкажіть імʼя</p>
          )}
        </div>

        <Input
          placeholder="По батькові (необовʼязково)"
          value={props.patronymic}
          onChange={(e) => props.onPatronymicChange(e.target.value)}
        />

        <div>
          <PhoneInput
            value={props.phone}
            onChange={props.onPhoneChange}
            className={attempted && phoneMissing ? 'border-warn' : ''}
          />
          {attempted && phoneMissing && (
            <p className="mt-1 text-[12px] text-warn">Вкажіть телефон</p>
          )}
        </div>

        <div>
          <CityAutocomplete value={props.city} onSelect={props.onCityChange} />
          {attempted && cityMissing && <p className="mt-1 text-[12px] text-warn">Вкажіть місто</p>}
        </div>

        {needsWarehouse && (
          <div>
            <WarehousePicker
              cityRef={props.city?.ref ?? null}
              value={props.warehouseRef}
              onSelect={props.onWarehouseChange}
            />
            {attempted && warehouseMissing && (
              <p className="mt-1 text-[12px] text-warn">Оберіть відділення</p>
            )}
          </div>
        )}
        {needsCourierAddress && (
          <>
            <div>
              <Input
                placeholder="Вулиця"
                value={props.street}
                onChange={(e) => props.onStreetChange(e.target.value)}
                className={attempted && streetMissing ? 'border-warn' : ''}
              />
              {attempted && streetMissing && (
                <p className="mt-1 text-[12px] text-warn">Вкажіть вулицю</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Input
                  placeholder="Будинок"
                  value={props.building}
                  onChange={(e) => props.onBuildingChange(e.target.value)}
                  className={attempted && buildingMissing ? 'border-warn' : ''}
                />
                {attempted && buildingMissing && (
                  <p className="mt-1 text-[12px] text-warn">Вкажіть будинок</p>
                )}
              </div>
              <Input
                placeholder="Квартира"
                value={props.apartment}
                onChange={(e) => props.onApartmentChange(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Поверх"
                value={props.floor}
                onChange={(e) => props.onFloorChange(e.target.value)}
              />
              <Input
                placeholder="Домофон"
                value={props.intercom}
                onChange={(e) => props.onIntercomChange(e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <div className="mt-6 space-y-2">
        {DELIVERY_OPTIONS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => props.onDeliveryKeyChange(option.key)}
            className={
              'block w-full border px-4 py-3 text-left text-[14px] cursor-pointer ' +
              (props.deliveryKey === option.key
                ? 'border-accent text-ink'
                : 'border-line text-muted')
            }
          >
            {option.label}
          </button>
        ))}
      </div>

      <Button variant="accent" size="lg" className="mt-6 w-full" onClick={handleNext}>
        ДАЛІ — ОПЛАТА
      </Button>
    </div>
  );
}
